import { Plus, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Switch } from "components/ui/switch";
import { USAGE_LIMITS_I18N } from "features/usage-config/constants/usage-meters";
import { cn } from "lib/utils";
import { ILimitWindow, LimitWindowType } from "features/usage-config/types/usage-enforcement.model";

import {
  clampBurst,
  clampDaysPerWindow,
  clampHours,
  clampWorkingDays,
  createLimitWindow,
  formatPercent,
  isClampedToMonth,
  rawMonthShare,
  windowKeyFromName,
} from "./limit-windows";
import { keyAsTyped } from "./usage-keys";
import { UsageKeyInput } from "./usage-key-input";
import { UsageCardFrame } from "./usage-card-frame";

const T = USAGE_LIMITS_I18N;

export interface LimitWindowsCardProps {
  windows: ILimitWindow[];
  onWindowsChange: (windows: ILimitWindow[]) => void;
  dimmed?: boolean;
  readOnly?: boolean;
  /** Keys held elsewhere in the same namespace -- the categories'. A window may not take one. */
  reservedKeys?: readonly string[];
  /** Why each window's key cannot be saved, by position; "" when it can. */
  keyErrors?: readonly string[];
}

export const LimitWindowsCard = ({
  windows,
  onWindowsChange,
  dimmed,
  readOnly,
  reservedKeys = [],
  keyErrors = [],
}: LimitWindowsCardProps) => {
  const { t } = useTranslation();
  // One button, like Add category. The row carries the fields, so nothing is filled in
  // before there is something to fill it into.
  const add = () => {
    const created = createLimitWindow(
      t(T.WINDOW_DEFAULT_NAME, { index: windows.length + 1 }),
      "daily",
      [...windows.map((limitWindow) => limitWindow.key), ...reservedKeys],
    );
    if (!created) return;
    onWindowsChange([...windows, created]);
  };

  // Found by position, never by key: a key is being typed, and until it is fixed two windows
  // may briefly hold the same one. Editing or removing by key would then hit both.
  const update = (index: number, patch: Partial<ILimitWindow>) =>
    onWindowsChange(
      windows.map((limitWindow, position) => {
        if (position !== index) return limitWindow;
        const next = { ...limitWindow, ...patch };
        const others = [
          ...windows.filter((_, other) => other !== index).map((other) => other.key),
          ...reservedKeys,
        ];
        // The key follows the name only while it is still the one made from that name.
        // A key someone typed stays put, the same rule a category key uses.
        if (
          patch.name !== undefined &&
          patch.key === undefined &&
          limitWindow.key === windowKeyFromName(limitWindow.name, others)
        ) {
          const derived = windowKeyFromName(patch.name, others);
          if (derived) next.key = derived;
        }
        if (patch.key !== undefined) next.key = keyAsTyped(patch.key);
        return next;
      }),
    );

  const remove = (index: number) =>
    onWindowsChange(windows.filter((_, position) => position !== index));

  return (
    <UsageCardFrame cardId="limit-windows" label={t(T.LIMIT_WINDOWS_TITLE)} dimmed={dimmed}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div>
          <h3 className="text-sm font-semibold">{t(T.LIMIT_WINDOWS_TITLE)}</h3>
          <p className="mt-1 max-w-prose text-sm leading-relaxed text-muted-foreground">
            {t(T.LIMIT_WINDOWS_DESCRIPTION)}
          </p>
          <p className="mt-2 max-w-prose text-xs leading-relaxed text-muted-foreground">
            {t(T.TENANT_ONLY_NOTE)}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={add}
          disabled={readOnly}
        >
          <Plus className="mr-1 h-4 w-4" />
          {t(T.WINDOW_ADD)}
        </Button>
      </div>

      {windows.length === 0 && (
        <p className="rounded-sm bg-muted px-3 py-2 text-sm text-muted-foreground">{t(T.WINDOW_EMPTY)}</p>
      )}

      {windows.map((limitWindow, index) => (
        <WindowEditor
          key={index}
          index={index}
          limitWindow={limitWindow}
          readOnly={readOnly}
          keyError={keyErrors[index] ?? ""}
          onChange={(patch) => update(index, patch)}
          onRemove={() => remove(index)}
        />
      ))}
    </UsageCardFrame>
  );
};

interface WindowEditorProps {
  index: number;
  limitWindow: ILimitWindow;
  readOnly?: boolean;
  keyError: string;
  onChange: (patch: Partial<ILimitWindow>) => void;
  onRemove: () => void;
}

const WindowEditor = ({
  index,
  limitWindow,
  readOnly,
  keyError,
  onChange,
  onRemove,
}: WindowEditorProps) => {
  const { t } = useTranslation();

  // The equation shows its real result. Past 100% the window is capped at the whole plan,
  // which the note below says -- writing 100% at the end of the sum would make it untrue.
  const percent = formatPercent(rawMonthShare(limitWindow));
  const formula =
    limitWindow.type === "hourly"
      ? t(T.WINDOW_FORMULA_HOURLY, {
          burst: limitWindow.burst,
          hours: limitWindow.hours,
          workingDays: limitWindow.working_days,
          percent,
        })
      : t(T.WINDOW_FORMULA_DAILY, {
          burst: limitWindow.burst,
          span: limitWindow.days_per_window,
          workingDays: limitWindow.working_days,
          percent,
        });

  return (
    <section
      aria-label={limitWindow.name}
      data-limit-window={limitWindow.key}
      className="flex flex-col gap-3 rounded-md border bg-muted/30 p-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-end gap-x-4 gap-y-3">
          <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          <div className="flex w-44 flex-col gap-1.5">
            <Label htmlFor={`usage-window-${index}-name`}>{t(T.WINDOW_NAME_LABEL)}</Label>
            <Input
              id={`usage-window-${index}-name`}
              className="h-9"
              value={limitWindow.name}
              disabled={readOnly}
              aria-label={t(T.WINDOW_RENAME, { name: limitWindow.name })}
              onChange={(event) => onChange({ name: event.target.value })}
            />
          </div>
          <div className="flex w-32 flex-col gap-1.5">
            <span className="text-sm font-medium leading-none">{t(T.WINDOW_TYPE_LABEL)}</span>
            <Select
              value={limitWindow.type}
              disabled={readOnly}
              onValueChange={(value) => onChange({ type: value as LimitWindowType })}
            >
              <SelectTrigger className="h-11" aria-label={t(T.WINDOW_TYPE_OF, { name: limitWindow.name })}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hourly">{t(T.WINDOW_TYPE_HOURLY)}</SelectItem>
                <SelectItem value="daily">{t(T.WINDOW_TYPE_DAILY)}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex w-52 flex-col gap-1.5">
            <Label htmlFor={`usage-window-${index}-key`}>{t(T.WINDOW_KEY)}</Label>
            <UsageKeyInput
              id={`usage-window-${index}-key`}
              inputClassName="h-9"
              value={limitWindow.key}
              readOnly={readOnly}
              label={t(T.WINDOW_KEY)}
              copyLabel={t(T.WINDOW_KEY_COPY)}
              hint={t(T.WINDOW_KEY_HINT)}
              error={keyError}
              onChange={(key) => onChange({ key })}
            />
          </div>
          </div>
          {limitWindow.enabled && (
            <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
              <NumberField
                id={`usage-window-${index}-days`}
                label={t(T.WINDOW_WORKING_DAYS)}
                suffix={t(T.WINDOW_DAYS_SUFFIX)}
                min={1}
                max={31}
                step={1}
                value={limitWindow.working_days}
                disabled={readOnly}
                onChange={(value) => onChange({ working_days: clampWorkingDays(value) })}
              />
              {limitWindow.type === "hourly" ? (
                <NumberField
                  id={`usage-window-${index}-hours`}
                  label={t(T.WINDOW_HOURS)}
                  suffix={t(T.WINDOW_HOURS_SUFFIX)}
                  min={1}
                  max={24}
                  step={1}
                  value={limitWindow.hours}
                  disabled={readOnly}
                  onChange={(value) => onChange({ hours: clampHours(value) })}
                />
              ) : (
                <NumberField
                  id={`usage-window-${index}-span`}
                  label={t(T.WINDOW_SPAN)}
                  suffix={t(T.WINDOW_DAYS_SUFFIX)}
                  min={1}
                  max={14}
                  step={1}
                  value={limitWindow.days_per_window}
                  disabled={readOnly}
                  onChange={(value) => onChange({ days_per_window: clampDaysPerWindow(value) })}
                />
              )}
              <NumberField
                id={`usage-window-${index}-burst`}
                label={t(T.WINDOW_BURST)}
                suffix={t(T.WINDOW_BURST_SUFFIX)}
                min={0.1}
                max={10}
                step={0.1}
                value={limitWindow.burst}
                disabled={readOnly}
                onChange={(value) => onChange({ burst: clampBurst(value) })}
              />
            </div>
          )}
        </div>
        <div className="flex shrink-0 flex-col gap-1.5">
          <span className="invisible select-none text-sm font-medium leading-none" aria-hidden="true">
            {t(T.WINDOW_ON)}
          </span>
          <div className="flex h-9 items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {limitWindow.enabled ? t(T.WINDOW_ON) : t(T.WINDOW_OFF)}
          </span>
          <Switch
            checked={limitWindow.enabled}
            onCheckedChange={(enabled) => onChange({ enabled })}
            disabled={readOnly}
            aria-label={t(T.WINDOW_ENABLED, { name: limitWindow.name })}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            title={t(T.WINDOW_REMOVE, { name: limitWindow.name })}
            aria-label={t(T.WINDOW_REMOVE, { name: limitWindow.name })}
            onClick={onRemove}
            disabled={readOnly}
          >
            <X className="h-4 w-4" />
          </Button>
          </div>
        </div>
      </div>

      {limitWindow.enabled ? (
        <>
          <p className="text-xs leading-relaxed text-muted-foreground">{formula}</p>
          {isClampedToMonth(limitWindow) && (
            <p className="rounded-md bg-background px-3 py-2 text-sm text-muted-foreground" role="status">
              {t(T.WINDOW_CLAMPED, { rawPercent: percent })}
            </p>
          )}
        </>
      ) : (
        <p className="text-sm text-muted-foreground">{t(T.WINDOW_OFF_NOTE, { name: limitWindow.name })}</p>
      )}
    </section>
  );
};

interface NumberFieldProps {
  id: string;
  label: string;
  suffix: string;
  min: number;
  max: number;
  step: number;
  value: number;
  disabled?: boolean;
  onChange: (value: number) => void;
}

const NumberField = ({ id, label, suffix, min, max, step, value, disabled, onChange }: NumberFieldProps) => (
  <div className="flex w-40 flex-col gap-1.5">
    <Label htmlFor={id}>{label}</Label>
    <div
      className={cn(
        "flex h-9 items-center rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background",
        disabled && "opacity-50",
      )}
    >
      <Input
        id={id}
        type="number"
        min={min}
        max={max}
        step={step}
        className="h-9 border-0 bg-transparent px-3 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 disabled:opacity-100 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      <span className="pointer-events-none shrink-0 pr-3 text-xs text-muted-foreground">{suffix}</span>
    </div>
  </div>
);
